import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import styles from './FundsOrigin.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { Form, Formik } from 'formik';
import Input from '@src/components/_shared/UI/Input';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import ValidateChoiceButton from '@src/components/_shared/UI/ValidateChoiceButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getReferencialData } from '@src/api/Referencial';
import Loader from '@src/components/_shared/UI/Loader';
import { useState } from 'react';
import DropdownList from '@src/components/_shared/UI/DropdownList';
import { companyUpdate } from '@src/api/Registration';
import { handleApiError } from '@src/utilities/functions';

const FUNDS_STEPS = {
    AVERAGETRANSFERCOUNT: 0,
    TOTALAMOUNT: 1,
    FUNDSORIGIN: 2,
    AVERAGEAMOUNT: 3
};

export default function AccountOptions() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [currentStep, setCurrentStep] = useState(FUNDS_STEPS.AVERAGETRANSFERCOUNT);

    const { data, isLoading } = useQuery({
        queryKey: ['combinedFundsData'],
        queryFn: async () => {
            const [averageTransferCountsData, averageTransferAmountsData, investAmountData, fundsSourcesData] = await Promise.all([
                getReferencialData({ type: 'investment/average-transfer-counts' }),
                getReferencialData({ type: 'investment/average-transfer-amounts' }),
                getReferencialData({ type: 'company/invest-amount' }),
                getReferencialData({ type: 'company/funds-sources' })
            ]);
            return { averageTransferCountsData, averageTransferAmountsData, investAmountData, fundsSourcesData };
        }
    });

    const _onGoBack = () => {
        setSection(5)
        setBlock('PriceList')
    };

    const { basic, investment } = registration.company;
    const { mutate, isPending } = useMutation({
        mutationFn: () => companyUpdate({ value: { basic, investment }, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }),
        onSuccess: () => {
            setBlock('Iban')
        },
        onError: (error: any) => handleApiError(error)
    });

    const goToNextStep = () => {
        setCurrentStep((prevStep) => prevStep + 1);
    };

    const goToPreviousStep = () => {
        setCurrentStep((prevStep) => (prevStep > 0 ? prevStep - 1 : prevStep));
    };

    const handleSelectAverageTransferCount = (value: string) => {
        setField('company.investment.transfer_count', value)
        goToNextStep()
    }

    const handleSelectInvestAmount = (value: string) => {
        setField('company.investment.invest_amount', value)
        goToNextStep()
    }

    const handleSelectAverageAmount = (value: string) => {
        setField('company.investment.average_transfer_amount', value)
        mutate();
    }

    if (isLoading) {
        return (
            <Container>
                <Loader type="SPINNER" />
            </Container>
        )
    }

    return (
        <Container>
            {currentStep === FUNDS_STEPS.AVERAGETRANSFERCOUNT &&
                <>
                    <BackButton onClick={_onGoBack}/>
                    <BlockTitle title="Veuillez indiquer une estimation du nombre de virements, entrants et sortants, que vous prévoyez de réaliser chaque année." />
                    <Subtitle content="Veuillez sélectionner l’estimation qui vous semble la plus appropriée" />
                    <div className={styles.buttonsList} style={{ height: '350px'}}>
                        {Object.entries(data?.averageTransferCountsData).map(([key, value]) => (
                            <ValidateChoiceButton
                                key={key}
                                title={value as string}
                                fullWidth
                                selected={registration.company.investment.transfer_count === key}
                                onValidate={() => handleSelectAverageTransferCount(key)}
                            />
                        ))}
                    </div>
                </>
            }
            {currentStep === FUNDS_STEPS.TOTALAMOUNT &&
                <>
                    <BackButton onClick={goToPreviousStep}/>
                    <BlockTitle title="Quelles sommes souhaitez-vous investir ?" />
                    <Subtitle content="Veuillez sélectionner la tranche de montant que vous souhaitez investir" />
                    <div className={styles.buttonsList} style={{ height: '200px'}}>
                        {Object.entries(data?.investAmountData).map(([key, value]) => (
                            <ValidateChoiceButton
                                key={key}
                                title={value as string}
                                fullWidth
                                selected={registration.company.investment.invest_amount === key}
                                onValidate={() => handleSelectInvestAmount(key)}
                            />
                        ))}
                    </div>
                </>
            }
            {currentStep === FUNDS_STEPS.FUNDSORIGIN &&
                <>
                    <BackButton onClick={goToPreviousStep}/>
                    <BlockTitle title="Origine des fonds" />
                    <Subtitle content="Veuillez préciser l’origine des fonds que vous souhaitez investir" />
                    <Formik
                        initialValues={{
                            foundSource: registration.company.investment.origin,
                            otherFoundSource: registration.company.investment.other_origin,
                        }}
                        onSubmit={(values) => {
                            setField('company.investment.origin', values.foundSource)
                            setField('company.investment.other_origin', values.otherFoundSource)
                            goToNextStep()
                        }}
                    >
                        {({ handleChange, handleSubmit, values }) => {
                            return (
                                <Form id="otherFoundsSourceForm" autoComplete="off" className="registerForm">
                                    <div style={{ width: '100%' }}>
                                        <DropdownList
                                            title="Origine des fonds"
                                            width="100%"
                                            options={data?.fundsSourcesData}
                                            selected={values.foundSource}
                                            onSelect={handleChange('foundSource')} />
                                    </div>
                                    {values.foundSource === '5' ?
                                        <Input
                                            title="Autre origine de fond"
                                            value={values.otherFoundSource}
                                            onChange={handleChange('otherFoundSource')}
                                            placeholder="Ex : vente d’un bien immobilier"
                                            id="otherFoundSource"
                                            name="otherFoundSource"
                                            type="text"
                                            width="100%"
                                            clearInput={() => handleChange('otherFoundSource')('')}
                                        />
                                        : null}
                                    <ValidateButton title="Continuer" disabled={!values.foundSource || (values.foundSource === '5' && !values.otherFoundSource)} onClick={handleSubmit} />
                                </Form>
                            )
                        }}
                    </Formik>
                </>
            }
            {currentStep === FUNDS_STEPS.AVERAGEAMOUNT &&
                <>
                    <BackButton onClick={goToPreviousStep}/>
                    <BlockTitle title="Quel sera le montant moyen approximatif des virements que vous souhaitez effectuer (à titre indicatif) ?" />
                    <Subtitle content="Veuillez sélectionner la tranche de montant correspondant à vos virements" />
                    {isPending ?
                        <Loader type="SPINNER" />
                        :
                        <div className={styles.buttonsList} style={{ height: '280px'}}>
                            {Object.entries(data?.averageTransferAmountsData).map(([key, value]) => (
                                <ValidateChoiceButton
                                    key={key}
                                    title={value as string}
                                    fullWidth
                                    selected={registration.company.investment.average_transfer_amount === key}
                                    onValidate={() => handleSelectAverageAmount(key)}
                                />
                            ))}
                        </div>
                    }
                </>
            }
        </Container>
    );
}
